import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeatureGate } from '../../hooks/useFeatureGates';
import { PATHS } from '../../paths';
import { useSidePanel } from '../../providers/SidePanelProvider';

/**
 * Open an alert in the assistant thread from a surface outside the assistant
 * pane — today the Alerts Center, at either size.
 *
 * Two steps, because the pane sits in two places. Recording the alert and
 * giving the `xl+` column back to the thread is the whole move at that size:
 * the pane is already mounted behind the Center and only has to unhide. Below
 * `xl` the two are separate routes, so the click also has to leave `/alerts` —
 * and the alert survives that navigation because it is held on the panel
 * context rather than inside either pane.
 *
 * Surface first, then navigate, matching `AlertsPane`'s X: it also pre-arms the
 * panel so a later widen lands on the thread rather than reopening the Center.
 *
 * **`feat-alerts-center` and `feat-assistant` are independent**, so a viewer can
 * hold the Center and no assistant at all. For them there is nowhere to open an
 * alert: at `xl+` the panel would derive an invisible surface and collapse the
 * column the Center was occupying, and below `xl` `/assistant` bounces to the
 * dashboard. The handler reports that it did nothing so the surface can fall
 * back, the same shape `getAlertRowAction` uses to decline a row.
 *
 * @returns A click handler taking the alert's id and whether the row was still
 *   unread when it was clicked, and answering whether it opened anything. The
 *   expand itself is issued by {@link useDrainPendingAlertExpand} once the pane
 *   is on screen.
 */
export function useOpenAlertInAssistant(): (alertId: string, wasUnread: boolean) => boolean {
  const navigate = useNavigate();
  const isXl = useBreakpoint('xl');
  const isAssistantEnabled = useFeatureGate(FeatureGate.Assistant);
  const { openAlertInAssistant } = useSidePanel();

  return React.useCallback(
    (alertId: string, wasUnread: boolean): boolean => {
      if (!isAssistantEnabled) {
        return false;
      }

      openAlertInAssistant(alertId, wasUnread);

      if (!isXl) {
        void navigate(PATHS.ASSISTANT);
      }

      return true;
    },
    [isAssistantEnabled, openAlertInAssistant, isXl, navigate]
  );
}
